/**
 * Master Access Logs — audit trail of login attempts, password resets,
 * impersonations and tenant lifecycle events. Filterable per tenant.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, RefreshCw, Filter } from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_PILL = {
  login_success:               { bg: "#DCFCE7", text: "#15803D", label: "Login OK" },
  login_failed:                { bg: "#FEE2E2", text: "#B91C1C", label: "Login fail" },
  tenant_created:              { bg: "#DBEAFE", text: "#1E40AF", label: "Tenant created" },
  tenant_password_reset:       { bg: "#FEF3C7", text: "#92400E", label: "Password reset" },
  tenant_cancelled:            { bg: "#F3F4F6", text: "#6B7280", label: "Tenant cancelled" },
  tenant_impersonation_started:{ bg: "#FAE8FF", text: "#86198F", label: "Impersonation" },
};

export default function MasterAccessLogs() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (tenantFilter) params.set("tenant_id", tenantFilter);
      const [logs, t] = await Promise.all([
        axios.get(`${API}/master/access-logs?${params.toString()}`),
        tenants.length === 0 ? axios.get(`${API}/master/tenants`) : Promise.resolve(null),
      ]);
      setItems(logs.data.items || []);
      if (t) setTenants(t.data.tenants || []);
    } catch (_e) {
      toast.error("Failed to load access logs");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantFilter, limit]);

  const tenantNameMap = useMemo(() => {
    const m = {};
    tenants.forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [tenants]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((it) => { c[it.action] = (c[it.action] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <MasterLayout title="Access Logs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">Access Logs</h2>
          <p className="text-[12.5px] text-white/55 mt-1">Audit login attempts, password resets, impersonations and tenant lifecycle events.</p>
        </div>
        <button
          data-testid="access-logs-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-[12.5px] font-bold hover:bg-white/5 text-white/85"
          style={{ borderColor: "rgba(201,162,78,0.4)" }}
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {Object.entries(counts).map(([action, n]) => {
          const pill = ACTION_PILL[action] || { bg: "#E5E7EB", text: "#374151", label: action };
          return (
            <div key={action} data-testid={`access-logs-count-${action}`}
              className="rounded-lg border px-3 py-2"
              style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
              {/* All stat-card labels render in master-gold for guaranteed
                  contrast against the dark Master Console background — the
                  per-action pill colors were tuned for LIGHT badges and were
                  invisible here. */}
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#C9A24E" }}>{pill.label}</div>
              <div className="text-[18px] font-extrabold text-white">{n}</div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <select
            data-testid="access-logs-tenant-filter"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-lg border bg-white/5 text-white text-[12.5px] focus:outline-none focus:border-[#C9A24E]"
            style={{ borderColor: "rgba(201,162,78,0.2)" }}
          >
            <option value="" className="bg-[#1A0E36]">All tenants</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id} className="bg-[#1A0E36]">{t.name} ({t.owner_email})</option>
            ))}
          </select>
        </div>
        <select
          data-testid="access-logs-limit"
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value, 10))}
          className="h-10 px-3 rounded-lg border bg-white/5 text-white text-[12.5px]"
          style={{ borderColor: "rgba(201,162,78,0.2)" }}
        >
          <option value={50} className="bg-[#1A0E36]">Last 50</option>
          <option value={100} className="bg-[#1A0E36]">Last 100</option>
          <option value={250} className="bg-[#1A0E36]">Last 250</option>
          <option value={500} className="bg-[#1A0E36]">Last 500</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/55 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border p-12 text-center text-white/55"
             style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
          <FileText className="w-8 h-8 mx-auto mb-2 text-white/30" />
          No access logs yet for this filter.
        </div>
      ) : (
        <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "rgba(201,162,78,0.12)" }}>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">When</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Action</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Tenant</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const pill = ACTION_PILL[it.action] || { bg: "#E5E7EB", text: "#374151", label: it.action };
                const det = it.details || {};
                const detKeys = Object.keys(det).filter((k) => k !== "tenant_id");
                return (
                  <tr key={it.id || idx} data-testid={`access-log-row-${idx}`}
                      className="border-b hover:bg-white/5 transition-colors"
                      style={{ borderColor: "rgba(201,162,78,0.08)" }}>
                    <td className="px-4 py-2.5 text-white/65 whitespace-nowrap text-[11px]">
                      {it.ts ? new Date(it.ts).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10.5px] font-bold px-2 py-0.5 rounded"
                            style={{ background: pill.bg, color: pill.text }}>{pill.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-white/75">
                      {it.tenant_id ? (tenantNameMap[it.tenant_id] || it.tenant_id.slice(0, 8) + "…") : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-white/65 max-w-md truncate" title={JSON.stringify(det)}>
                      {detKeys.map((k) => `${k}: ${typeof det[k] === "object" ? JSON.stringify(det[k]) : det[k]}`).join(" · ") || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </MasterLayout>
  );
}
